<template>
    <BaseModal :title="'Credential Management'" @close="closeModal">
        <template #modalContent>
            <div class="modal-content-wrapper">
                <div class="modal-content">
                    <div class="modal-heading">
                        <h3>Manage Access Credentials</h3>
                        <p class="modal-subheading">Issue and manage Verifiable Credentials for this cast</p>
                    </div>

                    <!-- Issue New Credential Section -->
                    <div class="section">
                        <div class="section-header">
                            <h4>//Issue Credential</h4>
                        </div>
                        <div class="form-content">
                            <div class="form-group">
                                <label>Wallet Address or DID</label>
                                <input
                                    v-model="newCredential.addressOrDid"
                                    type="text"
                                    placeholder="0x... or did:ethr:... or did:decast:..."
                                    :disabled="issuing"
                                    class="form-input"
                                />
                            </div>
                            <div class="form-row">
                                <div class="form-group">
                                    <label>Access Level</label>
                                    <select v-model="newCredential.accessLevel" :disabled="issuing" class="form-select">
                                        <option value="participant">Participant</option>
                                        <option value="speaker">Speaker</option>
                                        <option value="host">Host</option>
                                    </select>
                                </div>
                            </div>
                            <div class="form-group">
                                <label>Permissions</label>
                                <div class="permissions-grid">
                                    <label v-for="perm in availablePermissions" :key="perm.value" class="permission-checkbox">
                                        <input
                                            type="checkbox"
                                            :value="perm.value"
                                            v-model="newCredential.permissions"
                                            :disabled="issuing"
                                        />
                                        <span>{{ perm.label }}</span>
                                    </label>
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group">
                                    <label>Expires (Optional)</label>
                                    <input
                                        v-model="newCredential.expiresAt"
                                        type="datetime-local"
                                        :disabled="issuing"
                                        class="form-input"
                                        :min="minExpiryDate"
                                    />
                                    <span class="form-hint">Leave empty for no expiration</span>
                                </div>
                            </div>
                            <vs-button
                                class="custom-button"
                                :disabled="issuing || !isValidInput"
                                @click.stop="issueCredential">
                                {{ issuing ? '>>issuing...' : '>>issue credential' }}
                            </vs-button>
                        </div>
                    </div>

                    <!-- Bulk Issue Section -->
                    <div class="section">
                        <div class="section-header clickable" @click="showBulkForm = !showBulkForm">
                            <h4>//Bulk Issue</h4>
                            <span class="toggle-icon">{{ showBulkForm ? '[-]' : '[+]' }}</span>
                        </div>
                        <div v-if="showBulkForm" class="form-content">
                            <div class="form-group">
                                <label>Wallet Addresses (one per line)</label>
                                <textarea
                                    v-model="bulkAddresses"
                                    placeholder="0x1234...&#10;0x5678...&#10;did:ethr:0x..."
                                    rows="4"
                                    :disabled="bulkIssuing"
                                    class="form-textarea"
                                ></textarea>
                            </div>
                            <div class="form-group">
                                <label>Access Level (for all)</label>
                                <select v-model="bulkAccessLevel" :disabled="bulkIssuing" class="form-select">
                                    <option value="participant">Participant</option>
                                    <option value="speaker">Speaker</option>
                                    <option value="host">Host</option>
                                </select>
                            </div>
                            <vs-button
                                class="custom-button"
                                :disabled="bulkIssuing || !bulkAddresses.trim()"
                                @click.stop="bulkIssueCredentials">
                                {{ bulkIssuing ? `>>issuing ${bulkProgress}/${bulkTotal}...` : '>>issue to all' }}
                            </vs-button>
                        </div>
                    </div>

                    <!-- Issued Credentials List - Only show if there are credentials -->
                    <div class="section" v-if="credentials.length > 0 || loading">
                        <div class="section-header">
                            <h4>//Issued Credentials ({{ credentials.length }})</h4>
                            <vs-button class="refresh-button" @click="loadCredentials" :disabled="loading">
                                {{ loading ? '...' : 'refresh' }}
                            </vs-button>
                        </div>

                        <div v-if="loading" class="loading-state">
                            <div class="spinner"></div>
                            <p>Loading...</p>
                        </div>

                        <div v-else class="credentials-list">
                            <div
                                v-for="cred in credentials"
                                :key="cred.id"
                                class="credential-item"
                                :class="{ revoked: cred.revoked }"
                            >
                                <div class="credential-info">
                                    <div class="credential-did">{{ formatDid(cred.did) }}</div>
                                    <div class="credential-meta">
                                        <span class="badge" :class="cred.accessLevel">{{ cred.accessLevel }}</span>
                                        <span v-if="cred.status === 'revoked'" class="badge revoked">Revoked</span>
                                        <span v-if="cred.status === 'expired'" class="badge expired">Expired</span>
                                        <span class="date">{{ formatDate(cred.createdAt) }}</span>
                                        <span v-if="cred.expiresAt" class="expiry" :class="{ 'expiry-soon': isExpiringSoon(cred.expiresAt), 'expiry-past': isExpired(cred.expiresAt) }">
                                            {{ isExpired(cred.expiresAt) ? 'Expired' : 'Expires' }}: {{ formatDate(cred.expiresAt) }}
                                        </span>
                                        <span v-else class="expiry no-expiry">No expiry</span>
                                    </div>
                                </div>
                                <div class="credential-actions">
                                    <button
                                        v-if="!cred.revoked"
                                        class="revoke-button"
                                        @click="revokeCredential(cred)"
                                        :disabled="revoking === cred.id"
                                        title="Revoke credential"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <line x1="15" y1="9" x2="9" y2="15"/>
                                            <line x1="9" y1="9" x2="15" y2="15"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="cast-modal-bottom">
                    <p>>> Cast > Credential Management</p>
                </div>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import BaseModal from "@/components/common/BaseModal.vue";
import credentialsService from '@/services/credentialsService';

export default {
    name: 'CredentialManagementModal',
    props: ['closeModal', 'castDetails', 'setActiveModal'],
    components: {
        BaseModal,
    },
    data() {
        return {
            loading: false,
            issuing: false,
            revoking: null,
            bulkIssuing: false,
            bulkProgress: 0,
            bulkTotal: 0,
            showBulkForm: false,
            credentials: [],
            newCredential: {
                addressOrDid: '',
                accessLevel: 'participant',
                permissions: ['view', 'chat'],
                expiresAt: '',
            },
            bulkAddresses: '',
            bulkAccessLevel: 'participant',
            availablePermissions: [
                { value: 'view', label: 'View Cast' },
                { value: 'chat', label: 'Chat' },
                { value: 'share_screen', label: 'Share Screen' },
                { value: 'record', label: 'Record' },
                { value: 'manage_participants', label: 'Manage Participants' },
            ],
        };
    },
    computed: {
        isValidInput() {
            const input = this.newCredential.addressOrDid.trim();
            if (!input) return false;
            const isAddress = /^0x[a-fA-F0-9]{40}$/.test(input);
            // Accept any DID format: did:ethr:, did:decast:, etc.
            const isDid = /^did:[a-zA-Z0-9]+:.+$/.test(input);
            return isAddress || isDid;
        },
        roomId() {
            return this.castDetails?.public_meeting_id;
        },
        minExpiryDate() {
            // Minimum expiry date is now (for datetime-local input)
            const now = new Date();
            now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
            return now.toISOString().slice(0, 16);
        }
    },
    mounted() {
        document.getElementById('loading-bg-transparent').style.display = 'none';
        this.loadCredentials();
    },
    methods: {
        async loadCredentials() {
            if (!this.roomId) return;
            this.loading = true;
            try {
                const result = await credentialsService.getCredentialsByRoom(this.roomId);
                this.credentials = result.credentials || result || [];
            } catch (error) {
                console.error('Failed to load credentials:', error);
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to load credentials',
                    color: 'danger',
                });
            } finally {
                this.loading = false;
            }
        },

        getDid(addressOrDid) {
            const input = addressOrDid.trim();
            // If it's already a DID (any format), return as-is
            if (input.startsWith('did:')) {
                return input;
            }
            // Otherwise convert wallet address to did:ethr format
            return credentialsService.getDIDFromAddress(input);
        },

        async issueCredential() {
            this.issuing = true;
            document.getElementById('loading-bg-transparent').style.display = 'flex';
            document.getElementById('loading-bg-transparent-title').innerText = 'Issuing credential...';

            try {
                const did = this.getDid(this.newCredential.addressOrDid);
                // Convert datetime-local to ISO string if provided
                const expiresAt = this.newCredential.expiresAt
                    ? new Date(this.newCredential.expiresAt).toISOString()
                    : null;

                await credentialsService.issueCredential(
                    did,
                    this.roomId,
                    this.newCredential.accessLevel,
                    this.newCredential.permissions,
                    expiresAt
                );

                this.$vs.notify({
                    title: 'Success',
                    text: 'Credential issued successfully',
                    color: 'success',
                });

                this.newCredential.addressOrDid = '';
                this.newCredential.accessLevel = 'participant';
                this.newCredential.permissions = ['view', 'chat'];
                this.newCredential.expiresAt = '';

                await this.loadCredentials();
            } catch (error) {
                console.error('Failed to issue credential:', error);
                this.$vs.notify({
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to issue credential',
                    color: 'danger',
                });
            } finally {
                this.issuing = false;
                document.getElementById('loading-bg-transparent').style.display = 'none';
            }
        },

        async bulkIssueCredentials() {
            const addresses = this.bulkAddresses
                .split('\n')
                .map(a => a.trim())
                .filter(a => a.length > 0);

            if (addresses.length === 0) return;

            this.bulkIssuing = true;
            this.bulkProgress = 0;
            this.bulkTotal = addresses.length;

            document.getElementById('loading-bg-transparent').style.display = 'flex';
            document.getElementById('loading-bg-transparent-title').innerText = 'Bulk issuing credentials...';

            const results = { success: 0, failed: 0 };

            for (const address of addresses) {
                try {
                    const did = this.getDid(address);
                    await credentialsService.issueCredential(
                        did,
                        this.roomId,
                        this.bulkAccessLevel,
                        ['view', 'chat']
                    );
                    results.success++;
                } catch (error) {
                    console.error(`Failed to issue to ${address}:`, error);
                    results.failed++;
                }
                this.bulkProgress++;
            }

            this.$vs.notify({
                title: 'Bulk Issue Complete',
                text: `Success: ${results.success}, Failed: ${results.failed}`,
                color: results.failed > 0 ? 'warning' : 'success',
            });

            this.bulkAddresses = '';
            this.bulkIssuing = false;
            document.getElementById('loading-bg-transparent').style.display = 'none';
            await this.loadCredentials();
        },

        async revokeCredential(credential) {
            if (!confirm(`Revoke access for ${this.formatDid(credential.did)}?`)) {
                return;
            }

            this.revoking = credential.id;
            document.getElementById('loading-bg-transparent').style.display = 'flex';
            document.getElementById('loading-bg-transparent-title').innerText = 'Revoking credential...';

            try {
                await credentialsService.revokeCredential(credential.did, this.roomId, 'Revoked by cast owner');

                this.$vs.notify({
                    title: 'Success',
                    text: 'Credential revoked successfully',
                    color: 'success',
                });

                await this.loadCredentials();
            } catch (error) {
                console.error('Failed to revoke credential:', error);
                this.$vs.notify({
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to revoke credential',
                    color: 'danger',
                });
            } finally {
                this.revoking = null;
                document.getElementById('loading-bg-transparent').style.display = 'none';
            }
        },

        formatDid(did) {
            if (!did) return '';
            if (did.length <= 24) return did;
            return `${did.slice(0, 16)}...${did.slice(-8)}`;
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        },

        isExpired(dateString) {
            if (!dateString) return false;
            return new Date(dateString) < new Date();
        },

        isExpiringSoon(dateString) {
            if (!dateString) return false;
            const expiryDate = new Date(dateString);
            const now = new Date();
            const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
            return expiryDate > now && expiryDate <= threeDaysFromNow;
        },
    },
}
</script>

<style scoped>
.modal-content-wrapper {
    padding: 0 20px 20px 20px;
    max-height: 70vh;
    overflow-y: auto;
}

.modal-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.modal-heading h3 {
    color: #FFFFFF;
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 4px 0;
}

.modal-subheading {
    color: #545454;
    font-size: 12px;
    margin: 0;
}

.section {
    background: #0a0a0a;
    border: 1px solid #272727;
    padding: 16px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.section-header.clickable {
    cursor: pointer;
}

.section-header.clickable:hover h4 {
    color: #D7DF23;
}

.section-header h4 {
    color: #D7DF23;
    font-size: 13px;
    font-weight: 600;
    margin: 0;
    font-family: 'JetBrains Mono', monospace;
}

.toggle-icon {
    color: #545454;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
}

.form-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-row {
    display: flex;
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
}

.form-group label {
    color: #545454;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.form-input,
.form-select,
.form-textarea {
    background: #000000;
    border: 1px solid #272727;
    color: #FFFFFF;
    padding: 10px 12px;
    font-size: 13px;
    font-family: 'JetBrains Mono', monospace;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
    outline: none;
    border-color: #D7DF23;
}

.form-textarea {
    resize: vertical;
    min-height: 80px;
}

.permissions-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.permission-checkbox {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #a6a6a8;
    font-size: 12px;
    cursor: pointer;
}

.permission-checkbox input {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: #D7DF23;
}

.custom-button {
    background: #D7DF23 !important;
    color: #000000 !important;
    border: none !important;
    font-weight: 600;
    padding: 10px 20px;
    box-shadow: 3px 3px 0px 0px #545454;
    align-self: flex-start;
}

.custom-button:hover {
    box-shadow: 3px 3px 0px 0px #D7DF23;
}

.custom-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.refresh-button {
    background: transparent !important;
    color: #545454 !important;
    border: 1px solid #272727 !important;
    font-size: 11px;
    padding: 4px 12px;
}

.refresh-button:hover {
    border-color: #D7DF23 !important;
    color: #D7DF23 !important;
}

.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px;
    color: #545454;
}

.spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #272727;
    border-top-color: #D7DF23;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.credentials-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
}

.credential-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #000000;
    border: 1px solid #272727;
}

.credential-item.revoked {
    opacity: 0.5;
}

.credential-info {
    flex: 1;
}

.credential-did {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: #FFFFFF;
    margin-bottom: 6px;
}

.credential-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.badge {
    display: inline-block;
    padding: 2px 8px;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.badge.participant {
    background: rgba(59, 130, 246, 0.2);
    color: #60a5fa;
}

.badge.speaker {
    background: rgba(168, 85, 247, 0.2);
    color: #c084fc;
}

.badge.host {
    background: rgba(215, 223, 35, 0.2);
    color: #D7DF23;
}

.badge.revoked {
    background: rgba(239, 68, 68, 0.2);
    color: #EF4444;
}

.date {
    font-size: 10px;
    color: #545454;
}

.expiry {
    font-size: 10px;
    color: #22C55E;
}

.expiry.expiry-soon {
    color: #F59E0B;
}

.expiry.expiry-past {
    color: #EF4444;
}

.expiry.no-expiry {
    color: #545454;
}

.badge.expired {
    background: rgba(239, 68, 68, 0.2);
    color: #EF4444;
}

.form-hint {
    font-size: 10px;
    color: #545454;
    margin-top: 4px;
}

.credential-actions {
    display: flex;
    gap: 8px;
}

.revoke-button {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid #272727;
    color: #EF4444;
    cursor: pointer;
    transition: all 0.2s;
}

.revoke-button:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.1);
    border-color: #EF4444;
}

.revoke-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.cast-modal-bottom {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #272727;
}

.cast-modal-bottom p {
    color: #545454;
    margin: 0;
    font-size: 12px;
    font-family: 'JetBrains Mono', monospace;
}

/* Custom scrollbar */
.modal-content-wrapper::-webkit-scrollbar,
.credentials-list::-webkit-scrollbar {
    width: 4px;
}

.modal-content-wrapper::-webkit-scrollbar-track,
.credentials-list::-webkit-scrollbar-track {
    background: #0a0a0a;
}

.modal-content-wrapper::-webkit-scrollbar-thumb,
.credentials-list::-webkit-scrollbar-thumb {
    background: #272727;
}

.modal-content-wrapper::-webkit-scrollbar-thumb:hover,
.credentials-list::-webkit-scrollbar-thumb:hover {
    background: #D7DF23;
}
</style>
